import React, { useCallback, useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../../config';
import { getBearerAuthHeaders } from '../../utils/authFetchHeaders';
import ShareLinkEditor from './ShareLinkEditor';
import './ShareLinks.css';

/**
 * Brand settings → Public results links: links prospects can open without an
 * account (/s/<token>) showing the chosen strategies' equity chart and
 * headline numbers. Backtested results and the email step are opt-in.
 */

const json = () => ({ ...getBearerAuthHeaders(), 'Content-Type': 'application/json' });
const linkUrl = (token) => `${window.location.origin}/s/${token}`;

async function call(url, opts = {}) {
  const res = await fetch(url, { ...opts, headers: opts.body ? json() : getBearerAuthHeaders() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) throw new Error(data.error || 'Something went wrong.');
  return data;
}

function Leads({ id }) {
  const [leads, setLeads] = useState(null);
  useEffect(() => {
    call(API_ENDPOINTS.shareLinkLeads(id)).then((d) => setLeads(d.leads)).catch(() => setLeads([]));
  }, [id]);
  if (!leads) return <p className="bs-muted bs-small">Loading…</p>;
  if (!leads.length) return <p className="bs-muted bs-small">No emails yet.</p>;
  const copyAll = () => { try { navigator.clipboard.writeText(leads.map((l) => l.email).join('\n')); } catch (_e) { /* blocked */ } };
  return (
    <div className="shl-leads">
      <button type="button" className="bs-btn" onClick={copyAll}>Copy all {leads.length}</button>
      <ul>{leads.map((l) => <li key={l.email}>{l.email} <small>{new Date(l.at).toLocaleDateString()}</small></li>)}</ul>
    </div>
  );
}

export default function ShareLinks() {
  const [links, setLinks] = useState(null);
  const [strategies, setStrategies] = useState([]);
  const [unavailable, setUnavailable] = useState([]);
  const [options, setOptions] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [openLeads, setOpenLeads] = useState(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([call(API_ENDPOINTS.shareLinks), call(API_ENDPOINTS.shareLinkStrategies)]);
      setLinks(l.links);
      setStrategies(s.strategies);
      setUnavailable(Array.isArray(s.unavailable) ? s.unavailable : []);
      setOptions(s.options || null);
    } catch (err) {
      setError(err.message);
      setLinks([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (link, body) => {
    setError('');
    try {
      const { link: next } = await call(API_ENDPOINTS.shareLink(link.id), { method: 'PATCH', body: JSON.stringify(body) });
      setLinks((ls) => ls.map((l) => (l.id === link.id ? { ...next, leads: l.leads } : l)));
    } catch (err) { setError(err.message); }
  };
  const remove = async (link) => {
    if (!window.confirm(`Delete “${link.title}”? The link stops working for everyone who has it.`)) return;
    try {
      await call(API_ENDPOINTS.shareLink(link.id), { method: 'DELETE' });
      setLinks((ls) => ls.filter((l) => l.id !== link.id));
    } catch (err) { setError(err.message); }
  };
  // Links store data names; show each strategy under the name members see.
  const displayNameFor = (name) => strategies.find((s) => s.name === name)?.displayName || name;
  const copy = (link) => {
    try { navigator.clipboard.writeText(linkUrl(link.token)); setNote('Link copied.'); } catch (_e) { setNote(linkUrl(link.token)); }
  };

  return (
    <section className="bset-card" id="bset-share">
      <div className="shl-head">
        <div>
          <h3>Public results links</h3>
          <p className="bs-muted bs-small">A link anyone can open, no account needed: your chosen strategies’ equity chart, headline numbers and activity, with your checkout link to join if you choose. Send it to prospects.</p>
        </div>
        {!creating ? <button type="button" className="bs-btn bs-btn-primary" onClick={() => { setEditingId(null); setCreating(true); }}>New link</button> : null}
      </div>
      {note ? <p className="bs-note is-ok" role="status">{note}</p> : null}
      {error ? <p className="bs-note is-bad" role="alert">{error}</p> : null}
      {creating ? (
        <ShareLinkEditor
          previous={links?.[0] || null}
          strategies={strategies}
          unavailable={unavailable}
          options={options}
          onCancel={() => setCreating(false)}
          onSaved={(link) => { setCreating(false); setLinks((ls) => [link, ...(ls || [])]); copy(link); }}
        />
      ) : null}
      {links === null ? <p className="bs-muted bs-small">Loading…</p> : null}
      {links?.length === 0 && !creating ? <p className="bs-muted bs-small">No links yet.</p> : null}
      {links?.length ? (
        <ul className="shl-list">
          {links.map((l) => (
            <li key={l.id} className={`shl-item${l.disabled ? ' is-off' : ''}`}>
              <div className="shl-item-main">
                <strong>{l.title}</strong>
                <small>
                  {l.strategies.map((s) => `${displayNameFor(s.name)} ×${s.contracts}`).join(', ')}
                  {' · '}{l.showBacktested ? 'Live + backtested' : 'Live only'}
                  {l.emailGate ? ' · email first' : ''}
                  {l.expiresAt ? ` · expires ${new Date(l.expiresAt).toLocaleDateString()}` : ''}
                </small>
                <small>{l.views} view{l.views === 1 ? '' : 's'}{l.emailGate || l.leads ? ` · ${l.leads} email${l.leads === 1 ? '' : 's'}` : ''}{l.disabled ? ' · turned off' : ''}</small>
              </div>
              <div className="shl-actions">
                <button type="button" className="bs-btn" onClick={() => copy(l)} disabled={l.disabled}>Copy link</button>
                <button type="button" className="bs-btn" onClick={() => { setCreating(false); setEditingId(editingId === l.id ? null : l.id); }}>{editingId === l.id ? 'Close editor' : 'Edit'}</button>
                <a className="bs-btn" href={`/s/${l.token}`} target="_blank" rel="noreferrer">Open</a>
                <label className="shl-mini"><input type="checkbox" checked={l.showBacktested} onChange={(e) => patch(l, { showBacktested: e.target.checked })} /> Backtested</label>
                <label className="shl-mini"><input type="checkbox" checked={l.emailGate} onChange={(e) => patch(l, { emailGate: e.target.checked })} /> Email first</label>
                {l.leads ? <button type="button" className="bs-btn" onClick={() => setOpenLeads(openLeads === l.id ? null : l.id)}>Emails</button> : null}
                <button type="button" className="bs-btn" onClick={() => patch(l, { disabled: !l.disabled })}>{l.disabled ? 'Turn on' : 'Turn off'}</button>
                <button type="button" className="bs-btn shl-delete" onClick={() => remove(l)}>Delete</button>
              </div>
              {openLeads === l.id ? <Leads id={l.id} /> : null}
              {editingId === l.id ? (
                <div className="shl-edit">
                  <ShareLinkEditor
                    link={l}
                    strategies={strategies}
                    unavailable={unavailable}
                    options={options}
                    onCancel={() => setEditingId(null)}
                    onSaved={(next) => { setEditingId(null); setLinks((ls) => ls.map((x) => (x.id === next.id ? { ...next, leads: x.leads } : x))); setNote('Link updated.'); }}
                  />
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
